import {
  EntrancePlantDetailDTO,
  PlantSyncStatusDTO,
} from 'client-generator/generated-clients/plants';
import { Options, Vue } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING, SET_SYNC_INFO } from '@/core/store/action-types';
import DialogInfo from '@/views/components/DialogInfo/DialogInfo.vue';

enum BannerColor {
  blue = 'blue-banner',
  gray = 'gray-banner',
  orange = 'orange-banner',
}

@Options({
  name: 'EntrancePlantStatusBanner',
  components: { DialogInfo },
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
  },
  computed: {},
  emits: ['update-list'],
  watch: {},
})
export default class EntrancePlantStatusBanner extends Vue {
  bannerClass: string = '';
  enableSyncButton: boolean = false;
  isVisibleSyncButton: boolean = false;
  showRetryButton: boolean = false;
  showLastSyncData: boolean = false;
  showHelp: boolean = false;
  isVisibleHelpDialog: boolean = false;
  bannerDetails: string = '';
  BannerColor = BannerColor;
  title: string = '';
  subtitle: string = '';

  plant!: EntrancePlantDetailDTO;
  lastSyncDate: string = '';
  lastSyncTime: string = '';
  config = this.plant.syncStatus as PlantSyncStatusDTO;


  get needToSyncManually(): boolean {
    return (!this.config.canSyncRemotely || this.plant.gates?.some(g => g.devices?.some(d => d.needsToSyncManually)) || (this.config.syncInfo?.isFailed && !this.config.isRemoteSyncFailedAndRetryIsAllowed)) ?? false;
  }

  get virtualDevicesProblems(): boolean {
    return (!this.config.areVirtualDevicesSentToEliot || this.config.areVirtualDevicesChanged) ?? false;
  }

  get icon() {
    if ((this.config.isFullyConfigured && this.virtualDevicesProblems) || !this.config.synchronized || !this.config.connected)
      return {class: 'icon-warning', src: require(`@/assets/images/icons/icon-alert-medium-full.svg`), alt: 'icon-alert'};
    else 
      return {class: 'icon', src: require(`@/assets/images/icon-check.svg`), alt: 'icon-check'};
  }

  async mounted(): Promise<void> {
    if (this.config.lastSyncDateTime) {
      this.lastSyncDate = this.config.lastSyncDateTime.toLocaleDateString();
      this.lastSyncTime = this.config.lastSyncDateTime!.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' });
    }

    if (this.config.isPlantActive) {
      if (this.config.connected) {
        if (!this.config.synchronized) { 
          this.bannerClass = BannerColor.blue;
          this.showLastSyncData = true;
          this.bannerDetails = this.$t('AppResourceEP.LastSync') + ' ' + this.lastSyncDate + ' ' + this.lastSyncTime;
          this.isVisibleSyncButton = !this.needToSyncManually;
          this.enableSyncButton = true;
          this.showRetryButton = false;
          this.title = this.$t('AppResourceEP.NewChangesToSync');
          this.subtitle = this.needToSyncManually ? this.$t('PlantBannerNeedBTConnection') :  this.$t('AppResourceEP.ChangesToSync');
          this.showHelp = this.needToSyncManually;
        } else if (this.config.isFullyConfigured && this.virtualDevicesProblems) {
          this.bannerClass = BannerColor.orange;
          this.showLastSyncData = false;
          this.isVisibleSyncButton = false;
          this.showRetryButton = true;
          this.title = this.$t('AppResourceEP.IncompleteSaving');
          this.subtitle = this.$t('AppResourceEP.RetryToCompleteConfig');
        } else {
          this.bannerClass = BannerColor.blue;
          this.showLastSyncData = true;
          this.bannerDetails = this.$t('AppResourceEP.LastSync') + ' ' + this.lastSyncDate + ' ' + this.lastSyncTime;
          this.isVisibleSyncButton = true;
          this.enableSyncButton = false;
          this.showRetryButton = false;
          this.title = this.$t('general.synced');
          this.subtitle = this.$t('AppResourceEP.NoChangesToSync');
        }
      } else {
        this.isVisibleSyncButton = false;
        this.showRetryButton = false;
        if (!this.config.synchronized) {
          //plant active not connected and not synced
          this.bannerClass = BannerColor.gray
          this.title = this.$t('AppResourceEP.NewChangesToSync');
          this.subtitle = this.$t('PlantBannerNeedBTConnection');
          this.showLastSyncData = true;
          this.bannerDetails = this.$t('AppResourceEP.LastSync') + ' ' + this.lastSyncDate + ' ' + this.lastSyncTime;
          this.showHelp = true;
        } else if (this.config.isFullyConfigured && this.virtualDevicesProblems) {
          this.bannerClass = BannerColor.orange;
          this.showLastSyncData = false;
          this.showRetryButton = true;
          this.title = this.$t('AppResourceEP.IncompleteSaving');
          this.subtitle = this.$t('AppResourceEP.RetryToCompleteConfig');
        } else {
          //plant active not connected and synced
          this.bannerClass = BannerColor.gray;
          this.showLastSyncData = true;
          this.title = this.$t('PlantActiveNoConnectedTitle');
          this.bannerDetails = this.$t('AppResourceEP.LastSync') + ' ' + this.lastSyncDate + ' ' + this.lastSyncTime;
          this.showHelp = true;
        }
      }
    }
  }

  async onClickSyncNow() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const syncInfo = await plantsClient.syncwitheliot(this.plant.desktopId as number);
    
      this.$store.dispatch(SET_SYNC_INFO, syncInfo);
      if (syncInfo.isFailed) {
        this.$store.dispatch(SET_APP_LOADING, false);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$store.dispatch(SET_SYNC_INFO, null);
    }
  }

  async onClickRetry() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      await plantsClient.virtualdevices(this.plant.desktopId as number);

    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } 
    finally {
      this.$emit('update-list');
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  onClickHelp() {
    this.isVisibleHelpDialog = true;
  }

  toggleHelpDialog() {
    this.isVisibleHelpDialog = !this.isVisibleHelpDialog;
  }
}
